import { useMemo, useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { App, Button, Card, Empty, Space, Spin, Switch, Tag, Tooltip, Typography } from 'antd';
import { SaveOutlined, UndoOutlined, TableOutlined } from '@ant-design/icons';
import { get, put } from '../../../api/client';
import type { CompareResponse, CompareFeature } from '../types';

const cellKey = (level: string, feature: string) => `${level}::${feature}`;
const TEAL = '#0d9488';

/**
 * 等级功能权限矩阵（现代化重做，替代旧 Table+Switch 堆砌）。
 * - 功能按 groupName 分组，等级作为列，单元格为开关。
 * - 本地草稿 + 脏标记：改动集中「保存」时按等级批量覆盖（PUT level-features），避免每次切换都请求。
 * - body 发 camelCase（levelCode/featureKey），由后端 RequestInterceptor 转 snake_case 绑定 DTO。
 */
export function FeatureMatrix() {
  const { message } = App.useApp();
  const qc = useQueryClient();
  const { data, isLoading } = useQuery({
    queryKey: ['membership-compare'],
    queryFn: () => get<CompareResponse>('/admin/membership/level-features/compare'),
  });

  const levels = useMemo(() => data?.levels ?? [], [data]);
  const features = useMemo(() => data?.features ?? [], [data]);

  const baseline = useMemo(() => {
    const m: Record<string, boolean> = {};
    for (const f of features) for (const a of f.access) m[cellKey(a.levelCode, f.key)] = a.allowed;
    return m;
  }, [features]);

  const [draft, setDraft] = useState<Record<string, boolean>>({});
  const readCell = (k: string) => draft[k] ?? baseline[k] ?? false;

  const dirtyLevels = useMemo(() => {
    const set = new Set<string>();
    for (const k of Object.keys(draft)) {
      if (draft[k] !== baseline[k]) set.add(k.split('::')[0]);
    }
    return set;
  }, [draft, baseline]);
  const isDirty = dirtyLevels.size > 0;

  const save = useMutation({
    mutationFn: async () => {
      const targets = [...dirtyLevels];
      await Promise.all(
        targets.map((level) =>
          put('/admin/membership/level-features', {
            levelCode: level,
            features: features.map((f) => ({ featureKey: f.key, allowed: readCell(cellKey(level, f.key)) })),
          }),
        ),
      );
      return targets;
    },
    onSuccess: (saved) => {
      message.success(`已保存 ${saved.length} 个等级的权限配置`);
      setDraft({});
      void qc.invalidateQueries({ queryKey: ['membership-compare'] });
    },
    onError: (e) => message.error(e instanceof Error ? e.message : '保存失败'),
  });

  const groups = useMemo(() => {
    const map = new Map<string, CompareFeature[]>();
    for (const f of features) {
      const g = f.groupName || '其他';
      const arr = map.get(g);
      if (arr) arr.push(f);
      else map.set(g, [f]);
    }
    return [...map.entries()];
  }, [features]);

  if (isLoading) {
    return (
      <Card>
        <div style={{ textAlign: 'center', padding: 48 }}>
          <Spin />
        </div>
      </Card>
    );
  }
  if (!levels.length || !features.length) {
    return (
      <Card>
        <Empty description="暂无等级或功能权限数据，请先在上方创建等级与功能权限" />
      </Card>
    );
  }

  const gridCols = `minmax(280px, 2.2fr) repeat(${levels.length}, minmax(110px, 1fr))`;
  // 窄屏不许把等级列压扁：保住每列最小可读宽度，装不下就整块横向滚动
  const gridMinWidth = 280 + levels.length * 110;

  return (
    <Card
      title={
        <Space>
          <TableOutlined style={{ color: TEAL }} />
          <span>等级功能权限矩阵</span>
        </Space>
      }
      extra={
        <Space>
          {isDirty && (
            <Typography.Text type="warning" style={{ fontSize: 13 }}>
              {dirtyLevels.size} 个等级有未保存改动
            </Typography.Text>
          )}
          <Tooltip title="放弃全部改动">
            <Button icon={<UndoOutlined />} disabled={!isDirty} onClick={() => setDraft({})}>
              重置
            </Button>
          </Tooltip>
          <Button type="primary" icon={<SaveOutlined />} loading={save.isPending} disabled={!isDirty} onClick={() => save.mutate()}>
            保存改动
          </Button>
        </Space>
      }
    >
      <div style={{ border: '1px solid #eef0f1', borderRadius: 10, overflowX: 'auto' }}>
        {/* 表头 */}
        <div style={{ display: 'grid', gridTemplateColumns: gridCols, background: '#fafbfb', minWidth: gridMinWidth }}>
          <div style={{ padding: '12px 16px', fontWeight: 600, borderBottom: '1px solid #eef0f1' }}>功能 / 等级</div>
          {levels.map((l) => (
            <div
              key={l.code}
              style={{ padding: '10px 8px', textAlign: 'center', borderBottom: '1px solid #eef0f1', borderLeft: '1px solid #f2f3f4' }}
            >
              <Space size={4} align="center">
                <span style={{ fontWeight: 600 }}>{l.name}</span>
                {l.isDefault && (
                  <Tag color="teal" style={{ marginInlineEnd: 0, fontSize: 11, lineHeight: '16px' }}>
                    默认
                  </Tag>
                )}
              </Space>
              <div>
                <Typography.Text type="secondary" style={{ fontSize: 12, fontFamily: 'monospace' }}>
                  {l.code}
                </Typography.Text>
              </div>
            </div>
          ))}
        </div>

        {/* 分组 + 功能行 */}
        {groups.map(([groupName, rows]) => (
          <div key={groupName}>
            <div
              style={{
                padding: '6px 16px',
                background: '#f0faf8',
                fontSize: 12,
                fontWeight: 700,
                color: TEAL,
                letterSpacing: 0.6,
                borderTop: '1px solid #eef0f1',
                minWidth: gridMinWidth,
              }}
            >
              {groupName}
            </div>
            {rows.map((f) => (
              <div key={f.key} style={{ display: 'grid', gridTemplateColumns: gridCols, borderTop: '1px solid #f2f3f4', alignItems: 'center', minWidth: gridMinWidth }}>
                <div style={{ padding: '10px 16px' }}>
                  <div style={{ fontWeight: 500 }}>{f.name}</div>
                  <Typography.Text type="secondary" style={{ fontSize: 12, fontFamily: 'monospace' }}>
                    {f.key}
                  </Typography.Text>
                  {f.description && <div style={{ fontSize: 12, color: '#9aa0a6', marginTop: 2 }}>{f.description}</div>}
                </div>
                {levels.map((l) => {
                  const k = cellKey(l.code, f.key);
                  const changed = draft[k] !== undefined && draft[k] !== baseline[k];
                  return (
                    <div
                      key={l.code}
                      style={{
                        padding: '10px 8px',
                        textAlign: 'center',
                        borderLeft: '1px solid #f2f3f4',
                        background: changed ? '#fffbe6' : undefined,
                      }}
                    >
                      <Switch size="small" checked={readCell(k)} onChange={(c) => setDraft((d) => ({ ...d, [k]: c }))} />
                    </div>
                  );
                })}
              </div>
            ))}
          </div>
        ))}
      </div>
    </Card>
  );
}
